import ContractAuthenticationStatus from '@app/store/contract-authentication/ContractAuthenticationStatus';
import StatusAsAuthKey from '@app/store/contract-authentication/StatusAsAuthKey';
import {
  VerificationMethod,
  WithFailureProps,
} from '@app/store/transport-layer/types/verification';

import { getDocumentsParticipantsVerification } from '../verification';
import { getDocumentsParticipantsVerificationCheckKeyStatus } from '../verification/check-key-status';

interface DocumentsParticipantsAuthsResponse {
  authentications: Array<{
    authGuide: string | null;
    authType: 'D_CERT' | 'MOBILE_IDENTIFICATION' | 'PASSWORD';
    authenticationId: string;
    failureCount: number;
    lastFailedAt?: string | null;
    status?: ContractAuthenticationStatus;
  }>;
}

interface NewDocumentsParticipantsAuthsResponse {
  id: string;
  methods: WithFailureProps<VerificationMethod>[];
  participant: {
    id: string;
    dataLabel: string;
  };
  documentId: string;
}

export const createAuthGuideForMapping = (method?: VerificationMethod) => {
  if (!method) {
    return null;
  }
  if (method.type === 'MOBILE_IDENTIFICATION') {
    return `${method.displayName}|${method.displayPhoneNumber}`;
  }
  if (method.type === 'D_CERT') {
    return `${method.displayName}|${method.displayBizNumber}`;
  }
  if (method.type === 'PASSWORD') {
    return method.hint;
  }

  return null;
};

const convertToDeprecatedParticipantsAuthsResponse = (
  response: NewDocumentsParticipantsAuthsResponse | null
): DocumentsParticipantsAuthsResponse | null => {
  if (!response) {
    return null;
  }

  return {
    authentications: response.methods.map<{
      authGuide: string | null;
      authType: 'D_CERT' | 'MOBILE_IDENTIFICATION' | 'PASSWORD';
      authenticationId: string;
      failureCount: number;
      lastFailedAt?: string | null;
      status?: ContractAuthenticationStatus;
    }>((method) => {
      return {
        authGuide: createAuthGuideForMapping(method),
        authType: method.type,
        authenticationId: response.id,
        failureCount: method.failureCount,
        lastFailedAt: method.lastFailedAt,
        // 사용되지 않지만 컨버터에서 이전 api 동작을 명확히 하기 위해 전달합니다.
        status: undefined,
      };
    }),
  };
};

/**
 * GET 인증 상태 조회
 * @deprecated Endpoint path is forwarded in standardize-document-content
 * @see https://www.notion.so/modusign/bb8e24e723434ecc946dfecb85db5ee3#e39df5b009fc45aa8d1b5435495d5bcd
 */
export function getDocumentsParticipantsAuths({
  documentId,
  participantId,
  workspaceId,
}: {
  documentId: string;
  participantId: string;
  workspaceId: string;
}): Promise<DocumentsParticipantsAuthsResponse | null> {
  return getDocumentsParticipantsVerification({
    documentId,
    participantId,
    workspaceId,
  }).then((data) => convertToDeprecatedParticipantsAuthsResponse(data));
}

interface DocumentsParticipantsAuthsAuthenticationIdResponse {
  authType: 'MOBILE_IDENTIFICATION' | 'D_CERT';
  authenticationId: string;
  failureCount: number;
  lastFailedAt?: string;
  statusAsAuthKey: StatusAsAuthKey;
}
/**
 * GET 인증 키에 대한 인증 결과 및 상태 조회
 * @deprecated Endpoint is forwarded due to the endpoint path and response are changed on standardize-document-content
 * @see https://www.notion.so/modusign/bb8e24e723434ecc946dfecb85db5ee3#f2f1fe99c33845f8a90066668a6cfcfb
 */
export function checkDocumentsParticipantsAuthsAuthenticationStatusByType({
  authenticationType,
  authenticationKey,
  documentId,
  participantId,
  workspaceId,
}: {
  authenticationType: 'MOBILE_IDENTIFICATION' | 'D_CERT';
  authenticationKey: string;
  documentId: string;
  participantId: string;
  workspaceId: string;
}): Promise<DocumentsParticipantsAuthsAuthenticationIdResponse> {
  return getDocumentsParticipantsVerificationCheckKeyStatus({
    verificationKey: authenticationKey,
    documentId,
    participantId,
    workspaceId,
  }).then((response) => {
    const method = response.methods.find((m) => m.type === authenticationType) as WithFailureProps<
      VerificationMethod & { verificationKeyStatus: 'PENDING' | 'SUCCESS' }
    >;

    return {
      authenticationId: response.id,
      authType: authenticationType,
      statusAsAuthKey:
        method.verificationKeyStatus === 'PENDING'
          ? StatusAsAuthKey.PENDING
          : StatusAsAuthKey.SUCCESS,
      failureCount: method.failureCount,
      lastFailedAt: method.lastFailedAt ? method.lastFailedAt : undefined,
    };
  });
}
